import { skipToken } from "@reduxjs/toolkit/query/react";

import { customPaymentMethods } from "@jti/fe-common";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { CheckoutV2LoyaltyChoice, checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import {
  useGetAvailablePaymentMethodsQuery,
  useGetCustomerCartIdQuery,
  useSetPaymentMethodOnCartMutation,
} from "@jti/store/slices/magentoApiSlice";

export const useCartFreeOfCharge = () => {
  const dispatch = useDispatch();
  const { data: cartIdData } = useGetCustomerCartIdQuery(undefined);
  const cartId = cartIdData?.customerCart?.id;
  const { data: availablePaymentMethods, ...restPaymentMethodsQueryState } = useGetAvailablePaymentMethodsQuery(
    cartId ?? skipToken
  );
  const [setPaymentMethod] = useSetPaymentMethodOnCartMutation();
  const checkoutData = useSelector((state) => state.checkoutV2.data);
  const sectionsState = useSelector((state) => state.checkoutV2.sectionsState);
  const paymentSection = sectionsState?.paymentSection;
  const orderConfirmationSection = sectionsState?.orderConfirmationSection;
  const loyaltyChoice = useSelector((state) => state.checkoutV2.loyaltyPointsState?.loyaltyChoice);
  const shouldRequireLoyaltyRedemptionConfirmation = loyaltyChoice === CheckoutV2LoyaltyChoice.REDEEM;
  const hasMovedToOrderConfirmation = orderConfirmationSection === "active";

  const isCartFreeOfCharge = availablePaymentMethods
    ? availablePaymentMethods.some((method) => method.code === customPaymentMethods.zeroValueOrder)
    : undefined;

  const handleCartFreeOfCharge = () => {
    if (!cartId) {
      throw new Error("Cart ID is required to set payment method on cart");
    }

    setPaymentMethod({ cartId, code: customPaymentMethods.zeroValueOrder });

    dispatch(
      checkoutV2Slice.actions.setData({
        paymentMethodCode: customPaymentMethods.zeroValueOrder,
      })
    );

    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: hasMovedToOrderConfirmation ? "completed" : "editing",
        orderConfirmationSection: hasMovedToOrderConfirmation
          ? "active"
          : shouldRequireLoyaltyRedemptionConfirmation
            ? "rested"
            : "active",
      })
    );
  };

  const resetPaymentMethodCode = () => {
    dispatch(
      checkoutV2Slice.actions.setData({
        paymentMethodCode: undefined,
      })
    );
  };

  const handlePaymentSectionState = () => {
    dispatch(checkoutV2Slice.actions.setPaymentSectionState("editing"));
  };

  const handleOrderConfirmationSectionState = () => {
    dispatch(checkoutV2Slice.actions.setOrderConfirmationSectionState("active"));
  };

  const resetOrderConfirmationSectionState = () => {
    dispatch(checkoutV2Slice.actions.setOrderConfirmationSectionState("rested"));
  };

  if (isCartFreeOfCharge && checkoutData?.paymentMethodCode !== customPaymentMethods.zeroValueOrder) {
    handleCartFreeOfCharge();
  }

  if (isCartFreeOfCharge === false && checkoutData?.paymentMethodCode === customPaymentMethods.zeroValueOrder) {
    resetPaymentMethodCode();
  }

  if (isCartFreeOfCharge && !hasMovedToOrderConfirmation && paymentSection !== "editing") {
    handlePaymentSectionState();
  }

  if (isCartFreeOfCharge && !shouldRequireLoyaltyRedemptionConfirmation && !hasMovedToOrderConfirmation) {
    handleOrderConfirmationSectionState();
  }

  if (
    isCartFreeOfCharge &&
    shouldRequireLoyaltyRedemptionConfirmation &&
    !hasMovedToOrderConfirmation &&
    orderConfirmationSection !== "rested"
  ) {
    resetOrderConfirmationSectionState();
  }

  return {
    isCartFreeOfCharge,
    ...restPaymentMethodsQueryState,
  };
};
